import { OpenAPI } from "simplydo/interfaces";

import { API, APIError } from ".";
import SearchParams from "../searchParams";

export const challenges = (api: API) => ({
  getAll(
    params: OpenAPI.GET<"/challenges/all">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/all">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/challenges/all${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  create(
    name: string,
    success: (data: OpenAPI.POST<"/challenges">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/challenges", { name }, success, fail);
  },
  createWithData(
    data: Partial<OpenAPI.POST<"/challenges">["requestBody"]>,
    success: (data: OpenAPI.POST<"/challenges">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/challenges", data, success, fail);
  },
  createGatedChallenge(
    data: OpenAPI.POST<"/challenges/{challenge_id}/gatedChallenge/create">["requestBody"],
    id: string,
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/gatedChallenge/create">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/gatedChallenge/create`,
      data,
      success,
      fail,
    );
  },
  getAudienceSize(
    visibility: OpenAPI.POST<"/challenges/new/audience">["requestBody"]["visibility"],
    success: (
      data: OpenAPI.POST<"/challenges/new/audience">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/challenges/new/audience",
      { visibility },
      success,
      fail,
    );
  },
  get(
    id: string,
    success: (data: OpenAPI.GET<"/challenges/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest(
      "GET",
      `/challenges/${id}`,
      null,
      success,
      fail,
    );
  },
  getOverview(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/overview">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/overview">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/overview${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getProblems(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/problems">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/problems`,
      null,
      success,
      fail,
    );
  },
  getLabels(
    success: (data: OpenAPI.GET<"/challenges/labels">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("GET", `/challenges/labels`, null, success, fail);
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", `/challenges/${id}`, data, success, fail);
  },
  delete(
    id: string,
    success: (data: OpenAPI.DELETE<"/challenges/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}`,
      null,
      success,
      fail,
    );
  },
  setStatus(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/status">["requestBody"],
    success: (data: OpenAPI.PUT<"/challenges/{id}/status">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/status`,
      data,
      success,
      fail,
    );
  },
  updateStage(
    id: string,
    stage: string,
    openWithComms: boolean,
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/stage/{stage}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    if (!stage)
      api.authenticatedRequest(
        "DELETE",
        `/challenges/${id}/stage`,
        null,
        success,
        fail,
      );
    else
      api.authenticatedRequest(
        "PUT",
        `/challenges/${id}/stage/${stage}?openWithComms=${openWithComms}`,
        null,
        success,
        fail,
      );
  },
  getIdeas(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/ideas">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/ideas">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ideas${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getIdeaImpacts(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/ideas/impacts">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/ideas/impacts">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ideas/impacts${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getIdeaFilters(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/ideas/filters">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ideas/filters`,
      null,
      success,
      fail,
    );
  },
  getIdeaAssignees(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/ideas/assignees">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ideas/assignees`,
      null,
      success,
      fail,
    );
  },
  updateIdeasStatus(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/ideas/status">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ideas/status">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ideas/status`,
      data,
      success,
      fail,
    );
  },
  addIdeasLabels(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ideas/status/labels`,
      data,
      success,
      fail,
    );
  },
  removeIdeasLabels(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ideas/status/labels">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/ideas/status/labels`,
      data,
      success,
      fail,
    );
  },
  getTemplate(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/template">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest(
      "GET",
      `/challenges/${id}/template`,
      null,
      success,
      fail,
    );
  },
  updateTemplate(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/template">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/template">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/template`,
      data,
      success,
      fail,
    );
  },
  checkTemplateUpdate(
    id: string,
    data: OpenAPI.POST<"/challenges/{id}/template/check">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{id}/template/check">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/template/check`,
      data,
      success,
      fail,
    );
  },
  getPostable(
    challenge: string,
    success: (
      data: OpenAPI.GET<"/organisations/postable/new">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/postable/new?challenge=${challenge || ""}`,
      null,
      success,
      fail,
    );
  },
  createIdea(
    id: string,
    name: string,
    success: (data: OpenAPI.POST<"/challenges/{id}/ideas">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest(
      "POST",
      `/challenges/${id}/ideas`,
      { name },
      success,
      fail,
    );
  },
  getReports(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/reports">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/reports`,
      null,
      success,
      fail,
    );
  },
  getChartData(
    id: string,
    data: OpenAPI.GET<"/challenges/{id}/charts">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/challenges/{id}/charts">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(data);
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/charts${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getOptions(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/charts/options">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/charts/options`,
      null,
      success,
      fail,
    );
  },
  assignAssessors(
    id: string,
    ideas: string[],
    users: string[],
    assessmentRoundId: string,
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/assessors">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/assessors`,
      { ideas, users, assessmentRoundId },
      success,
      fail,
    );
  },
  assignAssessorAssignments(
    id: string,
    assignments: OpenAPI.PUT<"/challenges/{id}/assessors/assignment">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/assessors/assignment">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/assessors/assignment`,
      assignments,
      success,
      fail,
    );
  },
  remindAssessor(
    challengeId: string,
    assessorId: string,
    assessmentRoundId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/assessors/{assessor_id}/remind">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${challengeId}/assessors/${assessorId}/remind`,
      { assessmentRoundId },
      success,
      fail,
    );
  },
  unassignAssessors(
    id: string,
    ideas: string[],
    users: string[],
    assessmentRoundId: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/assessors">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/assessors`,
      { ideas, users, assessmentRoundId },
      success,
      fail,
    );
  },
  getAssignedAssessments(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/assessments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/assessments`,
      null,
      success,
      fail,
    );
  },
  submitAllAssessments(
    id: string,
    assessmentRoundId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/assessments/{assessment_round_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/assessments/${assessmentRoundId}`,
      null,
      success,
      fail,
    );
  },
  connectService(
    id: string,
    con_type: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/connections/{con_type}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/connections/${con_type}`,
      null,
      success,
      fail,
    );
  },
  disconnectService(
    id: string,
    con_type: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/connections/{con_type}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/connections/${con_type}`,
      null,
      success,
      fail,
    );
  },
  syncDCSalesforce(
    id: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/dc-salesforce/sync">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/dc-salesforce/sync`,
      null,
      success,
      fail,
    );
  },
  follow(
    id: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  unfollow(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  followers(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/followers">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/followers?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getIpSignatureFields(
    success: (
      data: OpenAPI.GET<"/challenges/ipmanagement/fields">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      "/challenges/ipmanagement/fields",
      null,
      success,
      fail,
    );
  },
  getLatestIpStatement(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/ipstatements/statements">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ipstatements/statements`,
      null,
      success,
      fail,
    );
  },
  agreeToLatestIpStatement(
    id: string,
    statementId: string,
    data: OpenAPI.PUT<"/challenges/{id}/ipstatements/statements/{statement_id}/agreement">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ipstatements/statements/{statement_id}/agreement">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ipstatements/statements/${statementId}/agreement`,
      data,
      success,
      fail,
    );
  },
  getAgreementsForIPStatement(
    id: string,
    statementId: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/ipstatements/statements/{statement_id}/agreements">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/ipstatements/statements/${statementId}/agreements`,
      null,
      success,
      fail,
    );
  },
  updateIpStatementStatus(
    id: string,
    toEnable: boolean,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/ipstatements">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/ipstatements`,
      { enabled: toEnable },
      success,
      fail,
    );
  },
  addIpStatement(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/ipstatements/statements">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ipstatements/statements">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ipstatements/statements`,
      data,
      success,
      fail,
    );
  },
  removeIpStatement(
    id: string,
    statementId: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/ipstatements/statements/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/ipstatements/statements/${statementId}`,
      null,
      success,
      fail,
    );
  },
  setActiveIpStatement(
    id: string,
    statementId: string,
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/ipstatements/statements/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/ipstatements/statements/${statementId}`,
      {},
      success,
      fail,
    );
  },
  updateIpStatement(
    id: string,
    statementId: string,
    data: OpenAPI.POST<"/challenges/{id}/ipstatements/statements/{statement_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{id}/ipstatements/statements/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/ipstatements/statements/${statementId}`,
      data,
      success,
      fail,
    );
  },
  activity(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/activity">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/activity">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/activity?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getAnalytics(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/activity/analytics">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/activity/analytics">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/activity/analytics${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getAnalyticsBreakdowns(
    id: string,
    params: OpenAPI.GET<"/challenges/{id}/activity/analytics/breakdowns">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/activity/analytics/breakdowns">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/activity/analytics/breakdowns${queryParams}`,
      null,
      success,
      fail,
    );
  },
  createFromIdea(
    data: OpenAPI.POST<"/challenges/createFromIdea">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/createFromIdea">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/challenges/createFromIdea",
      data,
      success,
      fail,
    );
  },
  getAssessmentDashboard(
    id: string,
    options: OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams(options).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/assessments/dashboard${params}`,
      null,
      success,
      fail,
    );
  },
  getAssessor(
    id: string,
    assessorId: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/assessments/dashboard/{assessor_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/assessments/dashboard/${assessorId}`,
      null,
      success,
      fail,
    );
  },
  getPotentialAssessorUsers(
    id: string,
    options: OpenAPI.GET<"/challenges/{id}/assessors/potential/users">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/assessors/potential/users">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams(options).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/assessors/potential/users${params}`,
      null,
      success,
      fail,
    );
  },
  getPotentialAssessorEmails(
    id: string,
    options: OpenAPI.GET<"/challenges/{id}/assessors/potential/emails">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/assessors/potential/emails">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams(options).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/assessors/potential/emails${params}`,
      null,
      success,
      fail,
    );
  },
  addPotentialAssessors(
    id: string,
    users: string[],
    emails: string[],
    success: (
      data: OpenAPI.POST<"/challenges/{id}/assessors/potential">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/assessors/potential`,
      { users, emails },
      success,
      fail,
    );
  },
  removePotentialAssessors(
    id: string,
    users: string[],
    emails: string[],
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/assessors/potential">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/assessors/potential`,
      { users, emails },
      success,
      fail,
    );
  },
  genAIGenerateChallengeDetails(
    data: OpenAPI.POST<"/challenges/ai/details">["requestBody"],
    success: (data: OpenAPI.POST<"/challenges/ai/details">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/challenges/ai/details",
      data,
      success,
      fail,
    );
  },
  genAIGenerateTags(
    data: OpenAPI.POST<"/challenges/ai/tags">["requestBody"],
    success: (data: OpenAPI.POST<"/challenges/ai/tags">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/challenges/ai/tags",
      data,
      success,
      fail,
    );
  },
  genAIImagePrompt(
    data: OpenAPI.POST<"/challenges/ai/image_prompt">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/ai/image_prompt">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      "/challenges/ai/image_prompt",
      data,
      success,
      fail,
    );
  },
  getQuestions(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{challenge_id}/questions">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest(
      "GET",
      `/challenges/${id}/questions`,
      null,
      success,
      fail,
    );
  },
  createQuestion(
    id: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/questions">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions`,
      data,
      success,
      fail,
    );
  },
  updateQuestion(
    id: string,
    questionId: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/${questionId}`,
      data,
      success,
      fail,
    );
  },
  deleteQuestion(
    id: string,
    questionId: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{challenge_id}/questions/{question_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/questions/${questionId}`,
      null,
      success,
      fail,
    );
  },
  publishQuestion(
    id: string,
    questionId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/publish">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/${questionId}/publish`,
      null,
      success,
      fail,
    );
  },
  unpublishQuestion(
    id: string,
    questionId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/unpublish">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/${questionId}/unpublish`,
      null,
      success,
      fail,
    );
  },
  reorderQuestion(
    id: string,
    questionId: string,
    order: number,
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/{question_id}/order">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/${questionId}/order`,
      { order },
      success,
      fail,
    );
  },
  announceQuestions(
    id: string,
    data: { questionIds: string[] },
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/announce">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/announce`,
      data,
      success,
      fail,
    );
  },
  copyQuestions(
    id: string,
    fromChallengeId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/questions/copy">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/questions/copy?fromChallengeId=${fromChallengeId}`,
      null,
      success,
      fail,
    );
  },
  copy(
    id: string,
    success: (data: OpenAPI.POST<"/challenges/{id}/copy">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/copy`,
      null,
      success,
      fail,
    );
  },
  getImpacts(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/impacts">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/impacts`,
      null,
      success,
      fail,
    );
  },
  copyImpacts(
    id: string,
    fromChallengeId: string,
    success: (
      data: OpenAPI.POST<"/challenges/{id}/copy/impacts">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/copy/impacts?fromChallengeId=${fromChallengeId}`,
      null,
      success,
      fail,
    );
  },
  copyFields: (
    challengeId: string,
    data: OpenAPI.POST<"/challenges/{challenge_id}/fields/copy">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{challenge_id}/fields/copy">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) => {
    api.authenticatedRequest(
      "POST",
      `/challenges/${challengeId}/fields/copy`,
      data,
      success,
      fail,
    );
  },
  reel: (
    success: (data: OpenAPI.GET<"/challenges/reel">["response"]) => void,
    fail: (error: APIError) => void,
  ) => {
    return api.authenticatedRequest(
      "GET",
      "/challenges/reel",
      null,
      success,
      fail,
    );
  },
  searchIdeas: (
    challengeId: string,
    queryOrOptions:
      | string
      | OpenAPI.GET<"/challenges/{id}/search/ideas">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/challenges/{id}/search/ideas">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) => {
    const options =
      typeof queryOrOptions === "string"
        ? { query: queryOrOptions }
        : queryOrOptions;
    const queryParams = new SearchParams({
      query: options.query,
      trace: options.trace,
      limit: options.limit,
    });
    return api.authenticatedRequest(
      "GET",
      `/challenges/${challengeId}/search/ideas${queryParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getChallengeFormat: (
    id: string,
    success: (data: OpenAPI.GET<"/challenges/{id}/format">["response"]) => void,
    fail: (error: APIError) => void,
  ) => {
    return api.authenticatedRequest(
      "GET",
      `/challenges/${id}/format`,
      null,
      success,
      fail,
    );
  },
  getChallengeFormatReview: (
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/format/review">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) => {
    return api.authenticatedRequest(
      "GET",
      `/challenges/${id}/format/review`,
      null,
      success,
      fail,
    );
  },
  getAutomations(
    id: string,
    success: (
      data: OpenAPI.GET<"/challenges/{id}/automations">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/challenges/${id}/automations`,
      null,
      success,
      fail,
    );
  },
  createAutomation(
    id: string,
    data: OpenAPI.POST<"/challenges/{id}/automations">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{id}/automations">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/automations`,
      data,
      success,
      fail,
    );
  },
  updateAutomation(
    id: string,
    automationId: string,
    data: OpenAPI.PUT<"/challenges/{id}/automations/{automation_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/automations/{automation_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/automations/${automationId}`,
      data,
      success,
      fail,
    );
  },
  deleteAutomation(
    id: string,
    automationId: string,
    success: (
      data: OpenAPI.DELETE<"/challenges/{id}/automations/{automation_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/challenges/${id}/automations/${automationId}`,
      null,
      success,
      fail,
    );
  },
  reorderAutomations(
    id: string,
    data: OpenAPI.PUT<"/challenges/{id}/automations/order">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/challenges/{id}/automations/order">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/challenges/${id}/automations/order`,
      data,
      success,
      fail,
    );
  },
  generateAutomationFilter(
    id: string,
    automationId: string,
    data: OpenAPI.POST<"/challenges/{id}/automations/{automation_id}/filter">["requestBody"],
    success: (
      data: OpenAPI.POST<"/challenges/{id}/automations/{automation_id}/filter">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/challenges/${id}/automations/${automationId}/filter`,
      data,
      success,
      fail,
    );
  },
});
